import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { api } from '../../services/api';
import {
  ServerIcon,
  PhoneIcon,
  CreditCardIcon,
  UserIcon,
  PlusIcon,
  ClockIcon,
  BarChart3Icon,
  AlertCircleIcon,
  CheckCircleIcon,
  XCircleIcon,
  TrendingUpIcon,
} from 'lucide-react';
import LoadingSpinner from '../../components/ui/LoadingSpinner';


interface Service {
  id: number;
  extension: string;
  password: string;
  name: string;
  description?: string;
  status: 'active' | 'inactive' | 'suspended';
  server_id: number;
  plan_id?: number;
  billing_cycle?: string;
  next_due_date?: string;
  phone_id?: string;
  meta_api_key?: string;
  whmcs_service_id?: number;
  whmcs_invoice_id?: number;
  whmcs_status?: string;
  created_at: string;
  updated_at: string;
  Server?: {
    id: number;
    name: string;
    url: string;
  };
  plan?: {
    id: number;
    name: string;
    price: number;
    concurrent_calls: number;
    monthly_calls?: number;
  };
}

interface DashboardStats {
  totalServices: number;
  activeServices: number;
  pendingInvoices: number;
  totalSpent: number;
}

const DashboardPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [services, setServices] = useState<Service[]>([]);
  const [stats, setStats] = useState<DashboardStats>({
    totalServices: 0,
    activeServices: 0,
    pendingInvoices: 0,
    totalSpent: 0,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setIsLoading(true);
      const [servicesResponse, invoicesResponse] = await Promise.all([
        api.get('/services'),
        api.get('/invoices'),
      ]);

      // Extract data from API response structure
      const servicesData = servicesResponse.data?.data?.services || [];
      const invoicesData = invoicesResponse.data?.data?.invoices || [];

      setServices(servicesData);

      // Calculate stats
      const activeServices = servicesData.filter((s: Service) => s.status === 'active').length;
      const pendingInvoices = invoicesData.filter((i: any) => i.status === 'pending' || i.status === 'Unpaid').length;
      const totalSpent = invoicesData
        .filter((i: any) => i.status === 'paid' || i.status === 'Paid')
        .reduce((sum: number, i: any) => sum + parseFloat(i.total || i.amount || 0), 0);

      setStats({
        totalServices: servicesData.length,
        activeServices,
        pendingInvoices,
        totalSpent,
      });
    } catch (err: any) {
      setError('Erro ao carregar dados do dashboard');
      console.error('Dashboard error:', err);
    } finally {
      setIsLoading(false);
    }
  };


  // Navigation handlers
  const handleNewService = () => {
    navigate('/services');
  };

  const handleViewServices = () => {
    navigate('/services');
  };

  const handleViewInvoices = () => {
    navigate('/invoices');
  };

  const handleViewProfile = () => {
    navigate('/profile');
  };


  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active':
        return <CheckCircleIcon className="h-4 w-4 text-green-500" />;
      case 'inactive':
        return <XCircleIcon className="h-4 w-4 text-red-500" />;
      case 'suspended':
        return <AlertCircleIcon className="h-4 w-4 text-yellow-500" />;
      default:
        return <AlertCircleIcon className="h-4 w-4 text-gray-500" />;
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'active':
        return 'Ativo';
      case 'inactive':
        return 'Inativo';
      case 'suspended':
        return 'Suspenso';
      default:
        return 'Desconhecido';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'inactive':
        return 'bg-red-100 text-red-800';
      case 'suspended':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };


  const extractServerHostname = (url: string) => {
    try {
      const urlObj = new URL(url);
      return urlObj.hostname;
    } catch {
      return url.replace(/^https?:\/\//, '').replace(/\/.*$/, '');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      {/* Elegant Header */}
      <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 mx-6 mt-6 overflow-hidden">
        <div className="px-8 py-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-2xl flex items-center justify-center mr-4 shadow-lg">
                <BarChart3Icon className="h-6 w-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">
                  Dashboard
                </h1>
                <p className="text-sm text-gray-600 mt-1">
                  Bem-vindo ao seu painel de controle WabbyCall
                </p>
              </div>
            </div>

            <div className="flex items-center space-x-4">
              <div className="text-right">
                <p className="text-sm font-semibold text-gray-900">Olá, {user?.name}!</p>
                <p className="text-xs text-gray-600">Último acesso: {new Date().toLocaleDateString('pt-BR')}</p>
              </div>
              <div className="w-12 h-12 bg-gradient-to-br from-cyan-100 to-blue-100 rounded-2xl flex items-center justify-center shadow-md">
                <UserIcon className="h-6 w-6 text-cyan-600" />
              </div>
            </div>
          </div>
        </div>
      </div>

        {error && (
          <div className="px-6 py-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-center">
                <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center mr-3">
                  <AlertCircleIcon className="h-4 w-4 text-red-600" />
                </div>
                <div className="flex-1">
                  <h3 className="text-sm font-semibold text-red-800 mb-1">Erro ao carregar dados</h3>
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Modern Stats Grid */}
        <div className="px-6 py-8">
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
            <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-6 hover:shadow-xl transition-all duration-300 hover:transform hover:scale-[1.02]">
              <div className="flex items-center">
                <div className="w-12 h-12 bg-gradient-to-br from-cyan-100 to-blue-100 rounded-2xl flex items-center justify-center mr-4 shadow-md">
                  <ServerIcon className="h-6 w-6 text-cyan-600" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-600 mb-1">Total de Serviços</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.totalServices}</p>
                </div>
              </div>
            </div>

            <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-6 hover:shadow-xl transition-all duration-300 hover:transform hover:scale-[1.02]">
              <div className="flex items-center">
                <div className="w-12 h-12 bg-gradient-to-br from-green-100 to-emerald-100 rounded-2xl flex items-center justify-center mr-4 shadow-md">
                  <CheckCircleIcon className="h-6 w-6 text-green-600" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-600 mb-1">Serviços Ativos</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.activeServices}</p>
                </div>
              </div>
            </div>

            <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-6 hover:shadow-xl transition-all duration-300 hover:transform hover:scale-[1.02]">
              <div className="flex items-center">
                <div className="w-12 h-12 bg-gradient-to-br from-yellow-100 to-orange-100 rounded-2xl flex items-center justify-center mr-4 shadow-md">
                  <CreditCardIcon className="h-6 w-6 text-yellow-600" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-600 mb-1">Faturas Pendentes</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.pendingInvoices}</p>
                </div>
              </div>
            </div>

            <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-6 hover:shadow-xl transition-all duration-300 hover:transform hover:scale-[1.02]">
              <div className="flex items-center">
                <div className="w-12 h-12 bg-gradient-to-br from-purple-100 to-indigo-100 rounded-2xl flex items-center justify-center mr-4 shadow-md">
                  <TrendingUpIcon className="h-6 w-6 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-600 mb-1">Total Gasto</p>
                  <p className="text-2xl font-bold text-gray-900">{formatCurrency(stats.totalSpent)}</p>
                </div>
              </div>
            </div>
          </div>
        </div>


        {/* Elegant Services Overview */}
        <div className="px-6 pb-8">
          <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 overflow-hidden">
            <div className="bg-gradient-to-r from-cyan-50 to-blue-50 px-8 py-6 border-b border-white/30">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className="w-10 h-10 bg-gradient-to-br from-cyan-100 to-blue-100 rounded-2xl flex items-center justify-center mr-4 shadow-md">
                    <PhoneIcon className="h-5 w-5 text-cyan-600" />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">Meus Serviços</h2>
                    <p className="text-sm text-gray-600 mt-1">Visão geral dos seus ramais ativos</p>
                  </div>
                </div>
                <button
                  onClick={handleViewServices}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-xl shadow-sm text-cyan-600 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500 transition-all duration-200"
                >
                  Ver Todos
                </button>
              </div>
            </div>

            <div className="p-8">
              {services.length === 0 ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-gradient-to-br from-cyan-100 to-blue-100 rounded-3xl flex items-center justify-center mx-auto mb-6 shadow-lg">
                    <PhoneIcon className="h-8 w-8 text-cyan-600" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-3">Nenhum serviço encontrado</h3>
                  <p className="text-sm text-gray-600 mb-6">
                    Você ainda não possui serviços ativos.
                  </p>
                  <button
                    onClick={handleViewServices}
                    className="inline-flex items-center px-6 py-3 border border-transparent text-sm font-semibold rounded-2xl shadow-lg text-white bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 hover:shadow-xl hover:transform hover:scale-105"
                  >
                    <PlusIcon className="h-5 w-5 mr-2" />
                    Contratar Serviço
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  {services.slice(0, 5).map((service) => (
                    <div key={service.id} className="flex items-center justify-between p-6 bg-gradient-to-r from-gray-50 to-white rounded-2xl hover:from-cyan-50 hover:to-blue-50 transition-all duration-300 hover:shadow-md border border-white/50">
                      <div className="flex items-center">
                        <div className="w-12 h-12 bg-gradient-to-br from-cyan-100 to-blue-200 rounded-2xl flex items-center justify-center mr-4 shadow-md">
                          <PhoneIcon className="h-6 w-6 text-cyan-600" />
                        </div>
                        <div>
                          <h4 className="text-base font-bold text-gray-900">
                            {service.name || `Ramal ${service.extension}`}
                          </h4>
                          <p className="text-sm text-gray-600 mt-1">
                            Ramal: {service.extension}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center">
                        {getStatusIcon(service.status)}
                        <span className={`ml-3 px-3 py-1.5 rounded-full text-xs font-semibold ${getStatusColor(service.status)} shadow-sm`}>
                          {getStatusText(service.status)}
                        </span>
                      </div>
                    </div>
                  ))}

                  {services.length > 5 && (
                    <div className="pt-6 border-t border-white/50">
                      <button
                        onClick={handleViewServices}
                        className="w-full text-center text-sm text-cyan-600 hover:text-cyan-800 font-semibold py-3 hover:bg-cyan-50 rounded-xl transition-colors duration-200"
                      >
                        Ver todos os serviços ({services.length})
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="px-6 pb-6">
          <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 overflow-hidden">
            <div className="bg-gradient-to-r from-gray-50 to-slate-50 px-6 py-4 border-b border-white/30">
              <div className="flex items-center">
                <div className="w-8 h-8 bg-gradient-to-br from-gray-100 to-slate-100 rounded-xl flex items-center justify-center mr-3 shadow-sm">
                  <ClockIcon className="h-4 w-4 text-gray-600" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">Ações Rápidas</h2>
                  <p className="text-sm text-gray-600">Acesse rapidamente as principais funcionalidades</p>
                </div>
              </div>
            </div>

            <div className="p-6">
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                <button
                  className="flex flex-col items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                  onClick={handleViewServices}
                >
                  <ServerIcon className="h-8 w-8 text-cyan-600 mb-2" />
                  <span className="text-sm font-medium text-gray-900">Serviços</span>
                </button>

                <button
                  className="flex flex-col items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                  onClick={handleViewInvoices}
                >
                  <CreditCardIcon className="h-8 w-8 text-yellow-600 mb-2" />
                  <span className="text-sm font-medium text-gray-900">Faturas</span>
                </button>

                <button
                  className="flex flex-col items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                  onClick={handleViewProfile}
                >
                  <UserIcon className="h-8 w-8 text-purple-600 mb-2" />
                  <span className="text-sm font-medium text-gray-900">Perfil</span>
                </button>
              </div>
            </div>
          </div>
        </div>
    </div>
  );
};

export default DashboardPage;